import type {
  CrmAccount,
  CrmAccountCreate,
  CrmAccountStatus,
  CrmAccountUpdate,
  EmployeeCountRange,
} from '@erp/shared';
import { Ban, Building2, Pencil, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, PhoneInput } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const STATUS_VARIANT: Record<CrmAccountStatus, 'success' | 'secondary'> = {
  active: 'success',
  inactive: 'secondary',
};

const EMPLOYEE_RANGES: EmployeeCountRange[] = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+'];

export function CrmAccountsPage(): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('crm.accounts.write');
  const [items, setItems] = useState<CrmAccount[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CrmAccount | null>(null);
  const [statusFilter, setStatusFilter] = useState<CrmAccountStatus | ''>('active');

  const reload = async (status: CrmAccountStatus | '' = statusFilter): Promise<void> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    const url = qs.length > 0 ? `/crm/accounts?${qs}` : '/crm/accounts';
    const r = await api().get<{ items: CrmAccount[] }>(url);
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const openEdit = (a: CrmAccount): void => {
    setShowCreate(false);
    setEditing(a);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeactivate = async (a: CrmAccount): Promise<void> => {
    if (!(await confirmDialog({ message: t('accounts.actions.confirmDeactivate', { code: a.code, name: a.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/crm/accounts/${a.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (a: CrmAccount): Promise<void> => {
    if (!(await confirmDialog({ message: t('accounts.actions.confirmReactivate', { code: a.code, name: a.name }) }))) return;
    try {
      await api().patch(`/crm/accounts/${a.id}`, { status: 'active' });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<CrmAccount>[] = [
    {
      id: 'code',
      header: t('accounts.columns.code'),
      sortValue: (a) => a.code,
      searchValue: (a) => a.code,
      cellClassName: 'font-mono text-xs',
      cell: (a) => a.code,
    },
    {
      id: 'name',
      header: t('accounts.columns.name'),
      sortValue: (a) => a.name,
      searchValue: (a) => a.name,
      cellClassName: 'font-medium',
      cell: (a) => a.name,
    },
    {
      id: 'industry',
      header: t('accounts.columns.industry'),
      sortValue: (a) => a.industry ?? '',
      searchValue: (a) => a.industry ?? '',
      cellClassName: 'text-fg-muted',
      cell: (a) => a.industry ?? '—',
    },
    {
      id: 'phone',
      header: t('accounts.columns.phone'),
      sortValue: (a) => a.phone ?? '',
      searchValue: (a) => a.phone ?? '',
      cellClassName: 'font-mono text-xs',
      cell: (a) => a.phone ?? '—',
    },
    {
      id: 'employees',
      header: t('accounts.columns.employees'),
      sortValue: (a) => a.employeeCountRange ?? '',
      cellClassName: 'text-fg-muted',
      cell: (a) => a.employeeCountRange ?? '—',
    },
    {
      id: 'status',
      header: t('accounts.columns.status'),
      sortValue: (a) => t(`accounts.status.${a.status}`),
      searchValue: (a) => t(`accounts.status.${a.status}`),
      cell: (a) => <Badge variant={STATUS_VARIANT[a.status]}>{t(`accounts.status.${a.status}`)}</Badge>,
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-44',
            cell: (a: CrmAccount) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(a)}
                  aria-label={`${t('accounts.actions.edit')} ${a.code}`}
                  title={t('accounts.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {a.status === 'active' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeactivate(a)}
                    aria-label={`${t('accounts.actions.deactivate')} ${a.code}`}
                    title={t('accounts.actions.deactivate')}
                  >
                    <Ban className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReactivate(a)}
                    aria-label={`${t('accounts.actions.reactivate')} ${a.code}`}
                    title={t('accounts.actions.reactivate')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('accounts.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('accounts.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('accounts.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreateAccountForm
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditAccountForm
          account={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CrmAccountStatus | '')}
          aria-label={t('accounts.statusFilter')}
        >
          <option value="">{t('accounts.allStatuses')}</option>
          <option value="active">{t('accounts.status.active')}</option>
          <option value="inactive">{t('accounts.status.inactive')}</option>
        </select>
      </div>

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Building2 className="h-8 w-8" aria-hidden />}
          title={t('accounts.empty')}
          description={t('accounts.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('accounts.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(a) => a.id}
            searchPlaceholder={t('accounts.searchPlaceholder')}
          />
        </Card>
      )}
    </div>
  );
}

interface CreateFormProps { onCancel: () => void; onCreated: () => void | Promise<void>; }

function CreateAccountForm({ onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<CrmAccountCreate>({ code: '', name: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/crm/accounts', form);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('accounts.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="a-code" label={t('accounts.create.code')} required>
          <Input id="a-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="a-name" label={t('accounts.create.name')} required>
          <Input id="a-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </FormField>
        <FormField id="a-industry" label={t('accounts.create.industry')}>
          <Input id="a-industry" value={form.industry ?? ''} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
        </FormField>
        <FormField id="a-website" label={t('accounts.create.website')}>
          <Input id="a-website" value={form.website ?? ''} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
        </FormField>
        <FormField id="a-phone" label={t('accounts.create.phone')}>
          <PhoneInput id="a-phone" value={form.phone ?? ''} onChange={(v) => setForm({ ...form, phone: v })} />
        </FormField>
        <FormField id="a-employees" label={t('accounts.create.employees')}>
          <select
            id="a-employees"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.employeeCountRange ?? ''}
            onChange={(e) => setForm({ ...form, employeeCountRange: (e.target.value || null) as EmployeeCountRange | null })}
          >
            <option value="">—</option>
            {EMPLOYEE_RANGES.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditAccountFormProps {
  account: CrmAccount;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function EditAccountForm({ account, onCancel, onSaved }: EditAccountFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  // Mirror the create form's fields. Code is read-only post-create —
  // it's the unique-per-tenant identifier and CrmAccountUpdateSchema
  // doesn't accept it anyway. Status flips happen via the per-row
  // Deactivate / Reactivate buttons, not as a dropdown here — same
  // shape used by items / parties / warehouses / contacts.
  // annualRevenue / revenueCurrencyCode / address / ownerUserId /
  // notes aren't surfaced (create form doesn't include them either).
  const [form, setForm] = useState<CrmAccountUpdate>({
    name: account.name,
    industry: account.industry,
    website: account.website,
    phone: account.phone,
    employeeCountRange: account.employeeCountRange,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: CrmAccountUpdate = {
        name: form.name?.trim() || undefined,
        // Empty optional text fields collapse to null so the server clears them.
        industry: form.industry && form.industry.trim() ? form.industry.trim() : null,
        website: form.website && form.website.trim() ? form.website.trim() : null,
        phone: form.phone && form.phone.trim() ? form.phone.trim() : null,
        employeeCountRange: form.employeeCountRange ?? null,
      };
      await api().patch(`/crm/accounts/${account.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('accounts.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('accounts.edit.title', { code: account.code, name: account.name })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="ae-code" label={t('accounts.create.code')} required>
          <Input
            id="ae-code"
            value={account.code}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="ae-name" label={t('accounts.create.name')} required>
          <Input
            id="ae-name"
            value={form.name ?? ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="ae-industry" label={t('accounts.create.industry')}>
          <Input
            id="ae-industry"
            value={form.industry ?? ''}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          />
        </FormField>
        <FormField id="ae-website" label={t('accounts.create.website')}>
          <Input
            id="ae-website"
            value={form.website ?? ''}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://…"
          />
        </FormField>
        <FormField id="ae-phone" label={t('accounts.create.phone')}>
          <PhoneInput id="ae-phone" value={form.phone ?? ''} onChange={(v) => setForm({ ...form, phone: v })} />
        </FormField>
        <FormField id="ae-employees" label={t('accounts.create.employees')}>
          <select
            id="ae-employees"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.employeeCountRange ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                employeeCountRange: (e.target.value || null) as EmployeeCountRange | null,
              })
            }
          >
            <option value="">—</option>
            {EMPLOYEE_RANGES.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('accounts.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{t('accounts.edit.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
